import React, { useState, useEffect } from "react"
import { useRecommendation } from "../contexts/RecommendationContext"
import ProductCard from "../components/ProductCard"
import RecipientSelector from "../components/RecipientSelector"

const Recommendations = () => {
  const [recipientInfo, setRecipientInfo] = useState({
    age: "",
    interests: "",
    relationship: "",
    gender: "",
    occasion: "",
    budget: "",
  })
  const [selectedRecipient, setSelectedRecipient] = useState(null)
  const [recommendations, setRecommendations] = useState([])
  const [searchingProducts, setSearchingProducts] = useState(false)
  const [searchProgress, setSearchProgress] = useState({ current: 0, total: 0, currentItem: "" })
  const [currentBuzzword, setCurrentBuzzword] = useState(0)
  const {
    generateRecommendations,
    generateAffiliateLink,
    savePurchase,
    loading,
    currentRecommendations,
    currentProducts,
    setCurrentProducts,
    preferences,
  } = useRecommendation()

  // Load stored data on component mount
  useEffect(() => {
    if (currentRecommendations && currentRecommendations.length > 0) {
      setRecommendations(currentRecommendations)

      // TEMPORARILY DISABLED: Auto-search for products
      // if (currentProducts.length === 0 && !loading && !searchingProducts) {
      //   searchForProducts(currentRecommendations)
      // }
    }
    // currentProducts is already managed globally, no need to set local state
  }, [currentRecommendations, currentProducts, loading, searchingProducts])

  // Pre-populate form with user preferences
  useEffect(() => {
    if (preferences) {
      setRecipientInfo(prev => ({
        ...prev,
        // Pre-fill relationship if user has a preferred one
        relationship: preferences.relationships?.[0] || prev.relationship,
        // Pre-fill occasion if user has a preferred one
        occasion: preferences.occasions?.[0] || prev.occasion,
        // Pre-fill budget based on user's typical price range
        budget: preferences.priceRanges?.[0]?.replace(/[^0-9-]/g, "").split("-")[1] || prev.budget,
        // Pre-fill interests based on user's categories
        interests: preferences.categories?.join(", ") || prev.interests,
      }))
    }
  }, [preferences])

  // Handle recipient selection
  const handleRecipientSelect = recipient => {
    setSelectedRecipient(recipient)
    if (recipient) {
      const updatedInfo = {
        ...recipientInfo,
        age: recipient.age?.toString() || recipientInfo.age,
        gender: recipient.gender || recipientInfo.gender,
        relationship: recipient.relationship || recipientInfo.relationship,
        interests: Array.isArray(recipient.interests) ? recipient.interests.join(", ") : recipient.interests || recipientInfo.interests,
      }
      setRecipientInfo(updatedInfo)
    }
  }

  const handleInputChange = e => {
    setRecipientInfo({
      ...recipientInfo,
      [e.target.name]: e.target.value,
    })
  }

  // Helper function to convert numeric age to age group
  const getAgeGroup = age => {
    if (!age) return ""
    const numAge = parseInt(age)
    if (numAge <= 12) return "child"
    if (numAge <= 17) return "teen"
    if (numAge <= 25) return "young-adult"
    if (numAge <= 50) return "adult"
    return "senior"
  }

  // Check if required fields are filled
  const isFormValid = selectedRecipient || (recipientInfo.relationship && recipientInfo.age)

  const handleSubmit = async e => {
    e.preventDefault()

    // Prevent submission if form is not valid
    if (!isFormValid) {
      return
    }

    // Clear previous results immediately
    setCurrentProducts([])
    setRecommendations([])

    // Prepare recipient data for AI recommendations
    const recipientData = selectedRecipient
      ? {
          age: getAgeGroup(selectedRecipient.age) || recipientInfo.age,
          interests: Array.isArray(selectedRecipient.interests)
            ? selectedRecipient.interests
            : selectedRecipient.interests?.split(",").map(i => i.trim()) || recipientInfo.interests.split(",").map(i => i.trim()),
          relationship: selectedRecipient.relationship || recipientInfo.relationship,
          gender: selectedRecipient.gender || recipientInfo.gender,
        }
      : {
          age: recipientInfo.age,
          interests: recipientInfo.interests.split(",").map(i => i.trim()),
          relationship: recipientInfo.relationship,
          gender: recipientInfo.gender,
        }

    const result = await generateRecommendations(recipientData, recipientInfo.occasion, parseFloat(recipientInfo.budget) || null, selectedRecipient)

    if (result.success) {
      // Extract the actual recommendations array
      const recommendationsArray = result.recommendations?.giftRecommendations || result.recommendations || []

      setRecommendations(recommendationsArray)

      // Search for products based on recommendations
      if (recommendationsArray.length > 0) {
        await searchForProducts(recommendationsArray)
      }
    } else {
      console.error("Failed to generate recommendations:", result.error)
    }
  }

  // Buzzwords for the loading carousel
  const buzzwords = [
    "🔍 Analyzing Amazon's vast catalog...",
    "🤖 AI-powered product matching...",
    "💎 Discovering premium gift options...",
    "⚡ Real-time price scanning...",
    "🎯 Personalizing recommendations...",
    "🛒 Curating perfect products...",
    "✨ Finding hidden gems...",
    "🎁 Crafting your gift list...",
  ]

  // Start buzzword carousel when searching
  const startBuzzwordCarousel = () => {
    setCurrentBuzzword(0)
    const interval = setInterval(() => {
      setCurrentBuzzword(prev => (prev + 1) % buzzwords.length)
    }, 1500) // Change every 1.5 seconds
    return interval
  }

  const searchForProducts = async recs => {
    setSearchingProducts(true)
    setCurrentProducts([]) // Clear existing products immediately

    // Start the buzzword carousel
    const buzzwordInterval = startBuzzwordCarousel()

    try {
      // Ensure recs is an array
      const recsArray = Array.isArray(recs) ? recs : []
      const allProducts = []
      const maxRecommendations = Math.min(recsArray.length, 4) // Use up to 4 recommendations

      // Initialize progress tracking for individual searches
      setSearchProgress({ current: 0, total: maxRecommendations, currentItem: "" })

      // Search for each individual product recommendation
      for (let i = 0; i < maxRecommendations; i++) {
        const rec = recsArray[i]

        if (rec.searchTerm) {
          // Update progress
          setSearchProgress({
            current: i + 1,
            total: maxRecommendations,
            currentItem: rec.product || rec.searchTerm,
          })

          // Add delay between API calls to respect rate limits
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 1500)) // 1.5 second delay
          }

          // Call the Amazon API directly to avoid context state updates during loop
          const apiUrl = process.env.NODE_ENV === "production" ? "/api/scrape-amazon" : "http://localhost:3001/api/scrape-amazon"
          const productResult = await fetch(`${apiUrl}?query=${encodeURIComponent(rec.searchTerm)}&type=search&limit=1`)
            .then(res => res.json())
            .then(data => ({
              success: data.success,
              products: data.products || [],
            }))
            .catch(error => ({
              success: false,
              products: [],
              error: error.message,
            }))

          if (productResult.success && productResult.products.length > 0) {
            const categorizedProducts = productResult.products.map(product => ({
              ...product,
              recommendationCategory: rec.category,
              recommendationReasoning: rec.reasoning,
            }))
            allProducts.push(...categorizedProducts)
          }
        }
      }

      // Apply budget filtering to actual Amazon products
      let filteredProducts = allProducts
      const budgetValue = parseFloat(recipientInfo.budget)
      if (budgetValue && budgetValue > 0) {
        const originalCount = allProducts.length
        filteredProducts = allProducts.filter(product => {
          // Extract price from product.price (e.g., "$369.00" -> 369.00)
          const priceStr = product.price || ""
          const priceMatch = priceStr.replace(/[,$]/g, "").match(/(\d+\.?\d*)/)
          if (priceMatch) {
            const price = parseFloat(priceMatch[1])
            const isValid = price <= budgetValue
            if (!isValid) {
              console.warn(`Filtered out product "${product.title}" - price $${price} exceeds budget $${budgetValue}`)
            }
            return isValid
          }
          // If we can't parse the price, keep the product (might be free or special pricing)
          return true
        })
        console.log(`Budget validation: ${originalCount} -> ${filteredProducts.length} products after filtering by $${budgetValue} budget`)
      }

      // Only set products after ALL searches are complete and budget filtering
      setCurrentProducts(filteredProducts)

      // Manually save to localStorage since we bypassed the context function
      if (filteredProducts.length > 0) {
        try {
          const storageKey = "guest" // Simplified for now, could be user-specific
          localStorage.setItem(`products_${storageKey}`, JSON.stringify(filteredProducts))
        } catch (error) {
          // Silent fail for localStorage errors
        }
      }
    } catch (error) {
      setCurrentProducts([]) // Clear products on error
    } finally {
      // Clear the buzzword carousel
      clearInterval(buzzwordInterval)
      setSearchingProducts(false)
      setSearchProgress({ current: 0, total: 0, currentItem: "" }) // Reset progress
      setCurrentBuzzword(0) // Reset buzzword
    }
  }

  const handlePurchase = async product => {
    // Generate affiliate link and track purchase
    try {
      const affiliateResult = await generateAffiliateLink(product.asin, product.url)
      if (affiliateResult.success) {
        // Save purchase record
        await savePurchase({
          product_id: product.asin,
          product_name: product.title,
          product_price: product.price,
          amazon_url: product.url,
          affiliate_link: affiliateResult.affiliateLink,
          recipient_info: JSON.stringify(recipientInfo),
          recipient_id: selectedRecipient?.id || null,
          recipient_name: selectedRecipient?.name || null,
        })

        // Open affiliate link
        window.open(affiliateResult.affiliateLink, "_blank", "noopener,noreferrer")
      } else {
        // Fallback to regular link
        window.open(product.url, "_blank", "noopener,noreferrer")
      }
    } catch (error) {
      // Fallback to regular link
      window.open(product.url, "_blank", "noopener,noreferrer")
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Recipient Information</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Recipient Selector */}
            <RecipientSelector onRecipientSelect={handleRecipientSelect} selectedRecipient={selectedRecipient} />

            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {selectedRecipient ? `Gift Details for ${selectedRecipient.name}` : "Gift Details"}
              </h3>
              {selectedRecipient && (
                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-700">
                    ✓ Recipient selected: <strong>{selectedRecipient.name}</strong>
                    {selectedRecipient.age && ` (${selectedRecipient.age} years old)`}
                    {selectedRecipient.relationship && ` - ${selectedRecipient.relationship}`}
                  </p>
                </div>
              )}
            </div>

            {/* Show recipient details fields only if no recipient is selected */}
            {!selectedRecipient && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Relationship <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="relationship"
                    value={recipientInfo.relationship}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select relationship</option>
                    <option value="mother">Mother</option>
                    <option value="father">Father</option>
                    <option value="spouse">Spouse/Partner</option>
                    <option value="sibling">Sibling</option>
                    <option value="friend">Friend</option>
                    <option value="colleague">Colleague</option>
                    <option value="child">Child</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Age Group <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="age"
                    value={recipientInfo.age}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select age group</option>
                    <option value="child">Child (0-12)</option>
                    <option value="teen">Teen (13-17)</option>
                    <option value="young-adult">Young Adult (18-25)</option>
                    <option value="adult">Adult (26-50)</option>
                    <option value="senior">Senior (50+)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                  <select
                    name="gender"
                    value={recipientInfo.gender}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Any</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-binary</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Interests (comma-separated)</label>
                  <input
                    type="text"
                    name="interests"
                    value={recipientInfo.interests}
                    onChange={handleInputChange}
                    placeholder="e.g., cooking, reading, technology, sports"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Occasion</label>
              <select
                name="occasion"
                value={recipientInfo.occasion}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select occasion</option>
                <option value="birthday">Birthday</option>
                <option value="holiday">Holiday</option>
                <option value="anniversary">Anniversary</option>
                <option value="graduation">Graduation</option>
                <option value="wedding">Wedding</option>
                <option value="just-because">Just Because</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Budget (optional)</label>
              <input
                type="number"
                name="budget"
                value={recipientInfo.budget}
                onChange={handleInputChange}
                placeholder="Enter budget in dollars"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !isFormValid}
              className={`w-full py-3 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 ${
                loading || !isFormValid ? "bg-gray-400 text-gray-600 cursor-not-allowed opacity-50" : "bg-purple-600 text-white hover:bg-purple-700"
              }`}
            >
              {loading ? "Generating Recommendations..." : !isFormValid ? "Please fill required fields" : "Get Recommendations"}
            </button>
          </form>
        </div>

        {/* Recommendations */}
        {/* <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Recommendations</h2>

          {!Array.isArray(recommendations) || recommendations.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              <p>Fill out the form to get personalized gift recommendations!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recommendations.map((rec, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">{rec.category}</h3>
                  <p className="text-gray-600 mb-2">{rec.reasoning}</p>
                  <p className="text-sm text-gray-500">Search: "{rec.searchTerm}"</p>
                  <p className="text-sm text-gray-500">Price Range: {rec.priceRange}</p>
                </div>
              ))}
            </div>
          )}
        </div> */}
      </div>

      {/* Products Section */}
      {currentProducts.length > 0 ? (
        <div className="mt-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Recommended Products</h2>
            <p className="text-lg text-gray-600">Based on your preferences, here are some great gift options</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {currentProducts.map((product, index) => (
              <ProductCard key={`${product.asin}-${index}`} product={product} onPurchase={handlePurchase} />
            ))}
          </div>
        </div>
      ) : loading || searchingProducts ? (
        <div className="mt-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">🎁 Curating Perfect Gifts</h2>
            <p className="text-lg text-gray-600">Our AI is working its magic...</p>
          </div>

          <div className="text-center py-12">
            {/* Main buzzword carousel */}
            <div className="mb-8">
              <div className="relative h-16 flex items-center justify-center">
                <div className="text-2xl font-bold text-purple-600 animate-pulse transition-all duration-500">{buzzwords[currentBuzzword]}</div>
              </div>
            </div>

            {/* Animated gift boxes */}
            <div className="flex justify-center items-center space-x-4 mb-8">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg animate-bounce transform rotate-12"></div>
              <div
                className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg animate-bounce transform -rotate-6"
                style={{ animationDelay: "0.2s" }}
              ></div>
              <div
                className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-500 rounded-lg animate-bounce transform rotate-45"
                style={{ animationDelay: "0.4s" }}
              ></div>
              <div
                className="w-6 h-6 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg animate-bounce transform -rotate-12"
                style={{ animationDelay: "0.6s" }}
              ></div>
            </div>

            {/* Progress ring */}
            <div className="relative w-32 h-32 mx-auto mb-6">
              <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="50" stroke="currentColor" strokeWidth="8" fill="none" className="text-gray-200" />
                <circle
                  cx="60"
                  cy="60"
                  r="50"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  className="text-purple-600"
                  style={{
                    strokeDasharray: `${2 * Math.PI * 50}`,
                    strokeDashoffset: `${2 * Math.PI * 50 * (1 - (searchProgress.total > 0 ? searchProgress.current / searchProgress.total : 0))}`,
                    transition: "stroke-dashoffset 0.5s ease-in-out",
                  }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{searchProgress.current}</div>
                  <div className="text-sm text-gray-500">of {searchProgress.total}</div>
                </div>
              </div>
            </div>

            {/* Current item being processed */}
            {searchProgress.currentItem && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
                <p className="text-purple-800 font-medium">🎯 Currently finding: {searchProgress.currentItem}</p>
              </div>
            )}

            {/* Fun facts */}
            <div className="text-sm text-gray-500 space-y-1">
              <p>✨ Finding 4 unique products</p>
              <p>🤖 AI-powered personalization</p>
              <p>⚡ Real-time Amazon data</p>
            </div>
          </div>
        </div>
      ) : recommendations.length > 0 ? (
        <div className="mt-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Gift Recommendations</h2>
            <p className="text-lg text-gray-600">AI-generated gift suggestions for you</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">No products found</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>We couldn't find specific products for these recommendations right now. You can search for them directly on Amazon:</p>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            {recommendations.map((rec, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{rec.product}</h3>
                    <p className="text-gray-600 mt-2">{rec.reasoning}</p>
                    <div className="mt-3 flex items-center space-x-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {rec.category}
                      </span>
                      <span className="text-sm text-gray-500">{rec.priceRange}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <a
                    href={`https://amazon.com/s?k=${encodeURIComponent(rec.searchTerm || rec.product)}&tag=givvio-20`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                  >
                    Search on Amazon
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default Recommendations
